import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, CardBody, Col, FormGroup, Input, Row } from "reactstrap";

import {
  DefaultButton,
  H3,
  MUIIcons,
  P,
  ToolTip,
} from "../../../../AbstractElements";
import { ALL_USERS_STATUS } from "../../../../Api/constants";
import DateRangeDisplay from "../../../../CommonElements/DateRangeDisplay";
import Divider from "../../../../CommonElements/Divider";
import FixedFooter from "../../../../CommonElements/FixedFooter";
import LabelTooltip from "../../../../CommonElements/LabelTooltip";
import StarLikertScale from "../../../../CommonElements/StarLikertScale";
import StatusDisplay from "../../../../CommonElements/StatusDisplay";
import CourseContentTable from "../../../../container/FeedbackCourseContent";
import { useAppDispatch, useAppSelector } from "../../../../ReduxToolkit/Hooks";
import {
  getCourseModuleFeedabck,
  postFeedback,
} from "../../../../ReduxToolkit/Reducers/FeedbackRatingSlice";
import { getScheduledCourseById } from "../../../../ReduxToolkit/Reducers/LoadFactorSlice";
import { getUsersList } from "../../../../ReduxToolkit/Reducers/UserSlice";
import {
  Enter,
  Instructor,
  isRequired,
  Location,
  Name,
  NextUsersFeedback,
  PleaseEnterPositiveErrorFor100,
  PleaseRateYourLevelAgreement,
  PracticalEnterPercentageToolTipMessage,
  PracticalPercentage,
  PreviousUsersFeedback,
  Room,
  Submit,
  SumMustBe100,
  SumOfTheoryAndPracticalError,
  TheoryEnterPercentageToolTipMessage,
  TheoryPercentage,
  ThisFieldRequired,
} from "../../../../utils/Constant";
import {
  formatString,
  handleKeyDownOnNumericWithoutDecimal,
} from "../../../../utils/helper/helper";
import "./style.scss";
import { hasPermissionToComponent } from "../../../../utils/helper/permission";

interface QuestionData {
  id: number;
  category: string;
  question_text: string;
  answer_type: string;
  response?: string;
}

const CourseFeedback = () => {
  const location = useLocation();
  const {
    course_schedule_id,
    review,
    course_feedback_id,
    participant_id,
    all_learners_feedback,
    participant_ind,
  } = location.state;
  const { scheduleCourseById } = useAppSelector(state => state.loadFactor);
  const [formValues, setFormValues] = useState({
    theoryPercentage: null,
    practicalPercentage: null,
    comment: "",
  });
  const [errors, setErrors] = useState({
    theoryPercentage: "",
    practicalPercentage: "",
  });
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [questionsData, setQuestionsData] = useState<QuestionData[]>([]);
  const { questionsList } = useAppSelector(state => state.feedback);
  const { usersList } = useAppSelector(state => state.user);
  const [userMap, setUserMap] = useState(null);
  const [currentParticipant, setCurrentParticipant] = useState(participant_ind);
  const participantName =
    userMap && participant_id
      ? userMap?.[participant_id]
      : userMap
        ? userMap?.[all_learners_feedback?.[currentParticipant]]
        : "";

  useEffect(() => {
    if (hasPermissionToComponent("USER_MANAGEMENT.ROOT")) {
      dispatch(getUsersList({ role: ALL_USERS_STATUS }));
    }
  }, [dispatch]);

  useEffect(() => {
    const userMapObj = {};
    usersList?.users?.forEach(user => {
      userMapObj[user.id] = user?.user_name;
    });
    setUserMap(userMapObj);
  }, [usersList]);

  useEffect(() => {
    dispatch(
      getCourseModuleFeedabck({
        type: "course",
        courseScheduleId: !review ? course_schedule_id : null,
        id: review && !participant_id ? course_feedback_id : null,
        schedule_id: !review ? course_schedule_id : null,
        participant_id:
          review && participant_id
            ? participant_id
            : all_learners_feedback
              ? all_learners_feedback[currentParticipant]
              : null,
      })
    );
  }, [dispatch, currentParticipant]);

  useEffect(() => {
    if (course_schedule_id) {
      dispatch(getScheduledCourseById(course_schedule_id));
    }
  }, [course_schedule_id]);

  const transformFeedbackData = data => {
    const updatedData = data?.feedback?.flatMap(feedbackItem =>
      Object.values(feedbackItem.overall_category).flatMap((category: any) =>
        category.question_responses.map(question => ({
          id: question.question_id,
          category: category.category,
          question_text: question.question_text,
          answer_type: question.response_type,
          response: question?.response,
        }))
      )
    );
    setQuestionsData(updatedData);
  };

  useEffect(() => {
    if (review && questionsList) {
      setFormValues({
        theoryPercentage: null,
        practicalPercentage: null,
        comment: questionsList?.feedback?.[0]?.overall_feedback_comment ?? "",
      });
      transformFeedbackData(questionsList);
    }
  }, [review, questionsList]);

  useEffect(() => {
    if (!review && questionsList?.questions?.length > 0) {
      setQuestionsData(questionsList.questions);
    }
  }, [questionsList]);

  const handleRating = (id: number, rate: number) => {
    setQuestionsData(prevData =>
      prevData.map(question =>
        question.id === id
          ? { ...question, response: rate.toString() }
          : question
      )
    );
  };

  const handleInputChange = (id: number, value: string) => {
    setQuestionsData(prevData =>
      prevData.map(question =>
        question.id === id ? { ...question, response: value } : question
      )
    );
  };

  const groupQuestionsByCategory = () => {
    return (
      questionsData?.reduce(
        (grouped, question) => {
          if (!grouped[question.category]) {
            grouped[question.category] = [];
          }
          grouped[question.category].push(question);
          return grouped;
        },
        {} as Record<string, QuestionData[]>
      ) || {}
    );
  };

  const handleChange = e => {
    const name = e.target.name;
    const value = e.target.value;

    const updateErrors = (errorMessage = "", resetValue = false) => {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: errorMessage,
      }));
      if (resetValue) {
        updateFormValues({ [name]: "" });
      }
    };

    const updateFormValues = updatedValues => {
      setFormValues(prevValues => ({
        ...prevValues,
        ...updatedValues,
      }));
    };

    if (name === "theoryPercentage" || name === "practicalPercentage") {
      const numberValue = parseInt(value, 10);

      if (isNaN(numberValue) || numberValue < 0 || numberValue > 100) {
        setErrors({
          ...errors,
          [name]: PleaseEnterPositiveErrorFor100,
        });
        updateFormValues({
          theoryPercentage: 0,
          practicalPercentage: 0,
        });
        return;
      }
      const isTheory = name === "theoryPercentage";
      const theoryTime = isTheory ? numberValue : 100 - numberValue;
      const practicalTime = 100 - theoryTime;

      if (theoryTime + practicalTime !== 100) {
        updateErrors(SumOfTheoryAndPracticalError, true);
        return;
      }

      updateErrors();
      updateFormValues({
        theoryPercentage: theoryTime,
        practicalPercentage: practicalTime,
      });
    } else {
      setFormValues(prevState => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  const handleError = () => {
    const newErrors = {
      theoryPercentage: "",
      practicalPercentage: "",
      questions: {},
    };

    let isError = false;

    const theory = parseInt(formValues.theoryPercentage) || 0;
    const practical = parseInt(formValues.practicalPercentage) || 0;

    if (
      formValues?.theoryPercentage === "" ||
      formValues?.theoryPercentage === null
    ) {
      newErrors.theoryPercentage = `${TheoryPercentage} ${isRequired}`;
      isError = true;
    }
    if (
      formValues.practicalPercentage === "" ||
      formValues?.practicalPercentage === null
    ) {
      newErrors.practicalPercentage = `${PracticalPercentage} ${isRequired}`;
      isError = true;
    }
    if (
      formValues.theoryPercentage &&
      formValues.practicalPercentage &&
      theory + practical !== 100
    ) {
      newErrors.theoryPercentage = SumMustBe100;
      isError = true;
    }

    setQuestionsData(prevData =>
      prevData.map(question => {
        if (!question.response) {
          isError = true;
          return {
            ...question,
            error: ThisFieldRequired,
          };
        }
        return { ...question, error: "" };
      })
    );

    setErrors(newErrors);

    if (isError) {
      return isError;
    }
  };

  const handleSubmit = async () => {
    const isErrrp = handleError();
    if (isErrrp) {
      return;
    }

    const payload = createPayload();

    const response = await dispatch(postFeedback({ payload }));

    if (response.payload) {
      navigate(-1);
    }
  };

  const createPayload = () => {
    return {
      course_schedule_id: course_schedule_id,
      theory_percent: parseInt(formValues.theoryPercentage),
      practical_percent: parseInt(formValues.practicalPercentage),
      question_responses: questionsData.map(question => {
        const response = question.response || "";
        return {
          question_id: question.id,
          response: response,
        };
      }),
    };
  };

  return (
    <div className="page-body course-feedback">
      <Card className="p-2">
        <CardBody className="mb-5">
          <div className="d-flex justify-content-between">
            <H3 className="fw-bold">{scheduleCourseById?.course_name}</H3>
            {all_learners_feedback && all_learners_feedback?.length > 1 && (
              <div className="d-flex gap-3">
                <div>
                  <span id="TooltipPreviousIcon" className="ml-2 pointer">
                    <MUIIcons
                      iconName="NavigateBeforeOutlined"
                      className="primary-icon-color pointer"
                      size={30}
                      onClick={() => {
                        if (currentParticipant > 0)
                          setCurrentParticipant(currentParticipant - 1);
                      }}
                    />
                  </span>
                  <ToolTip placement="top" target="TooltipPreviousIcon">
                    {PreviousUsersFeedback}
                  </ToolTip>
                </div>

                <div>
                  <span id="TooltipNextIcon" className="ml-2 pointer">
                    <MUIIcons
                      iconName="NavigateNextOutlined"
                      className="primary-icon-color pointer"
                      size={30}
                      onClick={() => {
                        if (
                          currentParticipant <
                          all_learners_feedback?.length - 1
                        )
                          setCurrentParticipant(currentParticipant + 1);
                      }}
                    />
                  </span>
                  <ToolTip placement="top" target="TooltipNextIcon">
                    {NextUsersFeedback}
                  </ToolTip>
                </div>
              </div>
            )}
          </div>
          <Row className="mt-3">
            <Col lg="4" className="mt-1">
              <DateRangeDisplay
                startDate={scheduleCourseById?.start_date}
                endDate={scheduleCourseById?.end_date}
              />
            </Col>
            <Col lg="8" className="d-flex gap-5 mt-3">
              <Row className="w-100">
                <Col lg="3" md="3" xs="12" sm="12">
                  {" "}
                  <StatusDisplay
                    label={Instructor}
                    number={
                      scheduleCourseById?.user_dict?.[
                        scheduleCourseById?.primary_trainer_id
                      ]?.user_name || "-"
                    }
                  />
                </Col>
                <Col lg="3" md="3" xs="12" sm="12">
                  <StatusDisplay
                    label={Location}
                    number={scheduleCourseById?.location_name}
                  />
                </Col>
                <Col lg="3" md="3" xs="12" sm="12">
                  <StatusDisplay
                    label={Room}
                    number={scheduleCourseById?.classroom_name}
                  />
                </Col>
                {participantName && participantName !== "" && (
                  <Col lg="3" md="3" xs="12" sm="12">
                    <StatusDisplay label={Name} number={participantName} />
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
          <Divider />
          {!review && (
            <P className="mt-1 mb-1">{PleaseRateYourLevelAgreement}</P>
          )}
          <div className="mt-2">
            <StarLikertScale />
          </div>

          {!review && (
            <div>
              <Row className="mt-3">
                <Col md="6">
                  <FormGroup>
                    <LabelTooltip
                      label={TheoryPercentage}
                      tooltipText={TheoryEnterPercentageToolTipMessage}
                      important={true}
                      placement={"bottom"}
                    />
                    <Input
                      type="number"
                      className="option-input"
                      name="theoryPercentage"
                      placeholder={`${Enter} ${TheoryPercentage}`}
                      value={formValues?.theoryPercentage}
                      onChange={handleChange}
                      maxLength={100}
                      onKeyDown={handleKeyDownOnNumericWithoutDecimal}
                      invalid={!!errors.theoryPercentage}
                      min={0}
                    />
                    {errors.theoryPercentage && (
                      <div className="invalid-feedback">
                        {errors.theoryPercentage}
                      </div>
                    )}
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <LabelTooltip
                      label={PracticalPercentage}
                      tooltipText={PracticalEnterPercentageToolTipMessage}
                      important={true}
                      placement={"bottom"}
                    />
                    <Input
                      type="number"
                      className="option-input"
                      name="practicalPercentage"
                      placeholder={`${Enter} ${PracticalPercentage}`}
                      value={formValues?.practicalPercentage}
                      onChange={handleChange}
                      maxLength={100}
                      onKeyDown={handleKeyDownOnNumericWithoutDecimal}
                      invalid={!!errors.practicalPercentage}
                      min={0}
                    />
                    {errors.practicalPercentage && (
                      <div className="invalid-feedback">
                        {errors.practicalPercentage}
                      </div>
                    )}
                  </FormGroup>
                </Col>
              </Row>
              <Divider />
            </div>
          )}
          <div className="mt-3">
            {Object.entries(groupQuestionsByCategory())?.map(
              ([category, questionsList]) => (
                <div className="mb-4" key={category}>
                  <CourseContentTable
                    key={category}
                    questions={questionsList}
                    handleRating={handleRating}
                    handleInputChange={handleInputChange}
                    category={formatString(category)}
                    review={review}
                  />
                </div>
              )
            )}
          </div>
          {!review && (
            <FixedFooter>
              <DefaultButton color="primary" onClick={handleSubmit}>
                {Submit}
              </DefaultButton>
            </FixedFooter>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default CourseFeedback;
